/**
 * Los tres idiomas y cómo se resuelve cuál se usa.
 *
 * El espejo exacto de `app/core/i18n.py`: mismos códigos, mismo default, misma
 * cadena de caída. Que los dos lados coincidan importa porque el idioma viaja
 * en `users.locale` y lo escriben ambos.
 *
 * **El idioma es de la persona, no del navegador ni de la URL.** Sale de
 * `locale` en `GET /auth/me` (o `GET /portal/me` para el contacto de cliente).
 * Si alguien eligió portugués en su perfil, lo ve en portugués desde cualquier
 * máquina, cosa que `navigator.language` no puede prometer.
 */

/** Los tres idiomas, con estos códigos exactos, en todo el stack. */
export const LOCALES = ['es', 'pt', 'en'] as const

export type Locale = (typeof LOCALES)[number]

/** El último recurso. Coincide con `LOCALE_POR_DEFECTO` del backend. */
export const DEFAULT_LOCALE: Locale = 'es'

function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value)
}

/**
 * El primer candidato que sea un idioma que hablamos.
 *
 * `pt-BR` y `es_MX` se normalizan a `pt` y `es`: el país no cambia el catálogo,
 * solo el formato de fecha, y eso se decide aparte. Un valor desconocido
 * (`fr`, `''`, `null`) **no es un error**: se ignora y se sigue bajando por la
 * cadena hasta `es`.
 */
export function resolveLocale(...candidates: readonly (string | null | undefined)[]): Locale {
  for (const candidate of candidates) {
    if (!candidate) continue
    const base = candidate.toLowerCase().replace('_', '-').split('-')[0]
    if (isLocale(base)) return base
  }
  return DEFAULT_LOCALE
}

/**
 * El tag BCP-47 con el que se le habla a `Intl`.
 *
 * Son regiones concretas y no el idioma pelado porque el formato lo decide la
 * región: `es` a secas da `18/9/2026` en algunas implementaciones y `es-AR` da
 * el `18/09/2026` que espera la gente que usa esto. Igual con los miles:
 * `es-AR` y `pt-BR` usan punto, `en-US` usa coma.
 */
export const BCP47: Record<Locale, string> = {
  es: 'es-AR',
  pt: 'pt-BR',
  en: 'en-US',
}

/** El nombre de cada idioma EN ese idioma: nadie busca "Spanish" en un menú en español. */
export const LOCALE_NATIVE_NAME: Record<Locale, string> = {
  es: 'Español',
  pt: 'Português',
  en: 'English',
}
